import { useMemo, useState } from 'react';
import { Header } from '../components/Header';
import {
  RoadmapEditModal,
  type RoadmapEditTarget,
} from '../components/RoadmapEditModal';
import { api } from '../lib/api';
import { useFactors } from '../lib/factors';
import { useRoadmapTemplates } from '../lib/roadmap';
import type { Factor } from '@shared/factors';
import type {
  Phase,
  RoadmapLevel,
  RoadmapTemplate,
  RoadmapTemplateOverride,
} from '@shared/roadmap';

const PHASE_CONFIG: Record<Phase, { name: string; pillClass: string; bgClass: string; textClass: string }> = {
  immediate: { name: '即応', pillClass: 'bg-rank-1 text-white', bgClass: 'bg-red-50', textClass: 'text-rank-1' },
  short_term: { name: '短期', pillClass: 'bg-rank-2 text-white', bgClass: 'bg-orange-50', textClass: 'text-rank-2' },
  mid_term: { name: '中期', pillClass: 'bg-rank-5 text-white', bgClass: 'bg-emerald-50', textClass: 'text-rank-5' },
};

const LEVEL_LABEL: Record<RoadmapLevel, string> = {
  1: '危機',
  2: '要改善',
  4: '良好',
  5: '最良',
};

const LEVEL_PILL: Record<RoadmapLevel, string> = {
  1: 'bg-rank-1 text-white',
  2: 'bg-rank-2 text-white',
  4: 'bg-rank-4 text-white',
  5: 'bg-rank-5 text-white',
};

type PhaseFilter = 'all' | Phase;
type LayerFilter = 'all' | 'I' | 'II' | 'III' | 'IV';

export default function RoadmapTemplates() {
  const { data: factorsData } = useFactors();
  const { data: templatesData, overrides, loading, refresh } = useRoadmapTemplates();
  const [phaseFilter, setPhaseFilter] = useState<PhaseFilter>('all');
  const [layerFilter, setLayerFilter] = useState<LayerFilter>('all');
  const [search, setSearch] = useState('');
  const [editTarget, setEditTarget] = useState<RoadmapEditTarget | null>(null);
  // We carry a side-channel of "what level/factor we're editing" so the save handler
  // knows which row to write back to. The modal target itself doesn't carry it.
  const [editKey, setEditKey] = useState<{ factor_id: number; level: RoadmapLevel } | null>(null);

  const overrideMap = useMemo(() => {
    const m = new Map<string, RoadmapTemplateOverride>();
    if (overrides) for (const o of overrides) m.set(`${o.factor_id}:${o.level}`, o);
    return m;
  }, [overrides]);

  const factorMap = useMemo(() => {
    const m = new Map<number, Factor>();
    if (factorsData) for (const f of factorsData.factors) m.set(f.id, f);
    return m;
  }, [factorsData]);

  const filteredTemplates = useMemo(() => {
    if (!templatesData || !factorsData) return [] as RoadmapTemplate[];
    const q = search.trim();
    return templatesData.templates.filter((t) => {
      if (phaseFilter !== 'all' && t.phase !== phaseFilter) return false;
      const f = factorMap.get(t.factor_id);
      if (!f) return false;
      if (layerFilter !== 'all' && f.layer !== layerFilter) return false;
      if (q) {
        const blob = `${f.id} ${f.name} ${f.category_name}`;
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [templatesData, factorsData, factorMap, phaseFilter, layerFilter, search]);

  const editedCount = overrides?.length ?? 0;

  const openEdit = (t: RoadmapTemplate) => {
    const f = factorMap.get(t.factor_id);
    if (!f) return;
    const override = overrideMap.get(`${t.factor_id}:${t.level}`);
    setEditKey({ factor_id: t.factor_id, level: t.level });
    setEditTarget({
      factor_id: f.id,
      factor_name: f.name,
      layer_name: f.layer_name,
      category_name: f.category_name,
      level: t.level,
      phase: t.phase,
      current_action: override?.custom_action ?? t.action,
      isCustomized: !!override,
      revertLabel: 'デフォルトに戻す',
    });
  };

  const handleSave = async (newAction: string) => {
    if (!editKey) return;
    await api.put(
      `/api/roadmap-templates/${editKey.factor_id}/${editKey.level}`,
      { custom_action: newAction },
    );
    await refresh();
  };

  const handleRevert = async () => {
    if (!editKey) return;
    await api.del(
      `/api/roadmap-templates/${editKey.factor_id}/${editKey.level}`,
    );
    await refresh();
  };

  const downloadJson = () => {
    if (!templatesData) return;
    // Build a merged JSON: bundled JSON with override actions substituted.
    const merged = {
      ...templatesData,
      updated_at: new Date().toISOString().slice(0, 10),
      templates: templatesData.templates.map((t) => {
        const o = overrideMap.get(`${t.factor_id}:${t.level}`);
        return o ? { ...t, action: o.custom_action } : t;
      }),
    };
    const blob = new Blob([JSON.stringify(merged, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `roadmap_templates_${merged.updated_at}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading || !templatesData || !factorsData) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="mx-auto max-w-6xl px-4 py-8 text-slate-400">読み込み中...</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">ロードマップ・テンプレート編集</h1>
            <p className="mt-1 text-sm text-slate-500">
              全{templatesData.templates.length}件 / 編集済み {editedCount}件 ・
              編集内容は全クライアントの新規ロードマップに反映されます。
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={downloadJson} className="btn-secondary">
              現在のJSONをダウンロード
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 text-sm">
            <span className="text-slate-500">フェーズ:</span>
            {(['all', 'immediate', 'short_term', 'mid_term'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPhaseFilter(p)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
                  phaseFilter === p
                    ? 'bg-brand text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {p === 'all' ? '全て' : PHASE_CONFIG[p].name}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-slate-500">レイヤー:</span>
            {(['all', 'I', 'II', 'III', 'IV'] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLayerFilter(l)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
                  layerFilter === l
                    ? 'bg-brand text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {l === 'all' ? '全て' : l}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="因子名・中分類で検索"
            className="input ml-auto max-w-xs text-sm"
          />
        </div>

        <p className="mb-3 text-xs text-slate-500">
          表示中 {filteredTemplates.length}件 / {templatesData.templates.length}件
        </p>

        <div className="space-y-2">
          {filteredTemplates.map((t) => {
            const f = factorMap.get(t.factor_id);
            if (!f) return null;
            const override = overrideMap.get(`${t.factor_id}:${t.level}`);
            const action = override?.custom_action ?? t.action;
            const cfg = PHASE_CONFIG[t.phase];

            return (
              <div
                key={`${t.factor_id}:${t.level}`}
                className="rounded-md border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="mb-2 flex flex-wrap items-baseline gap-2">
                  <span className={`rounded-md px-2 py-0.5 text-xs font-bold ${cfg.pillClass}`}>
                    {cfg.name}
                  </span>
                  <span className="text-xs text-slate-400">#{f.id}</span>
                  <span className="font-medium text-slate-800">{f.name}</span>
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${LEVEL_PILL[t.level]}`}
                  >
                    Lv.{t.level} {LEVEL_LABEL[t.level]}
                  </span>
                  <span className="text-xs text-slate-400">
                    {f.layer_name} / {f.category_name}
                  </span>
                  {override && (
                    <span className="ml-1 text-[10px] font-medium text-brand-accent">
                      (編集済み)
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => openEdit(t)}
                    className="ml-auto rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    編集
                  </button>
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                  {action}
                </p>
              </div>
            );
          })}

          {filteredTemplates.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
              フィルタ条件に一致するテンプレートがありません。
            </p>
          )}
        </div>
      </main>

      <RoadmapEditModal
        target={editTarget}
        onClose={() => {
          setEditTarget(null);
          setEditKey(null);
        }}
        onSave={handleSave}
        onRevert={handleRevert}
      />
    </div>
  );
}
